import type { User, Session } from '@supabase/supabase-js';

export interface MockAccount {
  email: string;
  role: 'admin' | 'admin_client' | 'client';
  fullName: string;
  userId: string;
}

// Mock user accounts for development
export const MOCK_ACCOUNTS: MockAccount[] = [
  {
    email: 'admin@example.com',
    role: 'admin',
    fullName: 'Admin User',
    userId: 'mock-admin-uuid-001'
  },
  {
    email: 'admin_user@example.com',
    role: 'admin_client',
    fullName: 'Manager User',
    userId: 'mock-manager-uuid-002'
  },
  {
    email: 'user@example.com',
    role: 'client',
    fullName: 'Regular User',
    userId: 'mock-client-uuid-003'
  }
];

export const isMockAuthEnabled = (): boolean => {
  return process.env.REACT_APP_USE_MOCK_AUTH === 'true';
};

export const getMockAccount = (email: string): MockAccount | undefined => {
  return MOCK_ACCOUNTS.find(acc => acc.email.toLowerCase() === email.toLowerCase());
};

export const createMockUser = (account: MockAccount): User => {
  return {
    id: account.userId,
    email: account.email,
    aud: 'authenticated',
    role: 'authenticated',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    app_metadata: {
      provider: 'email',
      role: account.role
    },
    user_metadata: {
      full_name: account.fullName,
      role: account.role
    },
    identities: [],
    factors: []
  } as User;
};

export const createMockSession = (account: MockAccount): Session => {
  const user = createMockUser(account);
  const now = Math.floor(Date.now() / 1000);
  
  return {
    access_token: `mock-access-token-${account.userId}`,
    refresh_token: `mock-refresh-token-${account.userId}`,
    expires_in: 3600,
    expires_at: now + 3600,
    token_type: 'bearer',
    user
  } as Session;
};

// Store mock session in localStorage
const MOCK_SESSION_KEY = 'pilbara-mock-session';

export const saveMockSession = (session: Session): void => {
  localStorage.setItem(MOCK_SESSION_KEY, JSON.stringify(session));
};

export const loadMockSession = (): Session | null => {
  try {
    const stored = localStorage.getItem(MOCK_SESSION_KEY);
    if (!stored) return null;
    
    const session = JSON.parse(stored) as Session;
    // Check if session is expired
    const now = Math.floor(Date.now() / 1000);
    if (session.expires_at && session.expires_at < now) {
      clearMockSession();
      return null;
    }
    
    return session;
  } catch {
    return null;
  }
};

export const clearMockSession = (): void => {
  localStorage.removeItem(MOCK_SESSION_KEY);
};

// Mock user list for admin dashboard
export interface MockUserSummary {
  id: string;
  email: string;
  fullName: string;
  role: 'admin' | 'admin_client' | 'client';
  createdAt: string;
  createdByUserId: string | null;
}

export const MOCK_USERS: MockUserSummary[] = [
  {
    id: 'mock-admin-uuid-001',
    email: 'admin@example.com',
    fullName: 'Admin User',
    role: 'admin',
    createdAt: new Date('2024-01-15').toISOString(),
    createdByUserId: null
  },
  {
    id: 'mock-manager-uuid-002',
    email: 'admin_user@example.com',
    fullName: 'Manager User',
    role: 'admin_client',
    createdAt: new Date('2024-02-20').toISOString(),
    createdByUserId: 'mock-admin-uuid-001'
  },
  {
    id: 'mock-client-uuid-003',
    email: 'user@example.com',
    fullName: 'Regular User',
    role: 'client',
    createdAt: new Date('2024-03-10').toISOString(),
    createdByUserId: 'mock-admin-uuid-001'
  },
  {
    id: 'mock-client-uuid-004',
    email: 'john.doe@example.com',
    fullName: 'John Doe',
    role: 'client',
    createdAt: new Date('2024-04-05').toISOString(),
    createdByUserId: 'mock-manager-uuid-002'
  },
  {
    id: 'mock-client-uuid-005',
    email: 'jane.smith@example.com',
    fullName: 'Jane Smith',
    role: 'client',
    createdAt: new Date('2024-05-12').toISOString(),
    createdByUserId: 'mock-manager-uuid-002'
  },
  {
    id: 'mock-manager-uuid-006',
    email: 'manager2@example.com',
    fullName: 'Second Manager',
    role: 'admin_client',
    createdAt: new Date('2024-06-18').toISOString(),
    createdByUserId: 'mock-admin-uuid-001'
  }
];

// Mock site records
export interface MockSite {
  id: string;
  name: string;
  location: string;
  createdAt: string;
  createdBy: string;
  drafts?: any[];
  accessibleBy?: string[];
}

export const MOCK_SITES: MockSite[] = [
  {
    id: 'mock-site-uuid-001',
    name: 'Pilbara Mining Site Alpha',
    location: 'Western Australia',
    createdAt: new Date('2024-01-20').toISOString(),
    createdBy: 'mock-admin-uuid-001',
    accessibleBy: ['mock-admin-uuid-001', 'mock-manager-uuid-002', 'mock-client-uuid-003'],
    drafts: [
      {
        id: 'mock-draft-uuid-001',
        siteId: 'mock-site-uuid-001',
        siteName: 'Pilbara Mining Site Alpha',
        siteLocation: 'Western Australia',
        riskLevel: 'High',
        completionPercentage: 85,
        createdAt: new Date('2024-01-25').toISOString(),
        updatedAt: new Date('2024-10-20').toISOString(),
        hazards: [
          {
            id: 'hazard-001',
            hazardType: 'Heavy Machinery',
            hazardStatus: 'Active',
            description: 'Excavators and dump trucks operating in close proximity',
            likelihood: 'Likely',
            consequence: 'Major',
            riskRating: 'High',
            controls: 'Spotters assigned, exclusion zones marked',
            createdAt: new Date('2024-01-25').toISOString()
          },
          {
            id: 'hazard-002',
            hazardType: 'Fall Hazards',
            hazardStatus: 'Controlled',
            description: 'Open pit edges without barriers',
            likelihood: 'Possible',
            consequence: 'Catastrophic',
            riskRating: 'Extreme',
            controls: 'Safety barriers installed, signage in place',
            createdAt: new Date('2024-02-10').toISOString()
          },
          {
            id: 'hazard-003',
            hazardType: 'Dust Exposure',
            hazardStatus: 'Active',
            description: 'High dust levels during dry season',
            likelihood: 'Almost Certain',
            consequence: 'Moderate',
            riskRating: 'High',
            controls: 'Water trucks operating, PPE required',
            createdAt: new Date('2024-03-05').toISOString()
          }
        ],
        mapRectangles: [
          {
            id: 'rect-001',
            topLeft: { lat: -20.5, lng: 116.8 },
            bottomRight: { lat: -20.52, lng: 116.82 },
            color: '#ff0000',
            hazardIds: ['hazard-001', 'hazard-002']
          }
        ]
      }
    ]
  },
  {
    id: 'mock-site-uuid-002',
    name: 'Iron Ore Processing Plant',
    location: 'Port Hedland, WA',
    createdAt: new Date('2024-02-15').toISOString(),
    createdBy: 'mock-manager-uuid-002',
    accessibleBy: ['mock-admin-uuid-001', 'mock-manager-uuid-002', 'mock-client-uuid-004'],
    drafts: [
      {
        id: 'mock-draft-uuid-002',
        siteId: 'mock-site-uuid-002',
        siteName: 'Iron Ore Processing Plant',
        siteLocation: 'Port Hedland, WA',
        riskLevel: 'Medium',
        completionPercentage: 65,
        createdAt: new Date('2024-02-20').toISOString(),
        updatedAt: new Date('2024-10-15').toISOString(),
        hazards: [
          {
            id: 'hazard-004',
            hazardType: 'Chemical Exposure',
            hazardStatus: 'Active',
            description: 'Processing chemicals in use',
            likelihood: 'Possible',
            consequence: 'Major',
            riskRating: 'High',
            controls: 'Chemical handling procedures, PPE mandatory',
            createdAt: new Date('2024-02-20').toISOString()
          },
          {
            id: 'hazard-005',
            hazardType: 'Noise Exposure',
            hazardStatus: 'Controlled',
            description: 'Crushing and conveyor operations',
            likelihood: 'Almost Certain',
            consequence: 'Moderate',
            riskRating: 'Medium',
            controls: 'Hearing protection zones, noise monitoring',
            createdAt: new Date('2024-03-01').toISOString()
          }
        ],
        mapRectangles: [
          {
            id: 'rect-002',
            topLeft: { lat: -20.31, lng: 118.58 },
            bottomRight: { lat: -20.33, lng: 118.60 },
            color: '#ff9900',
            hazardIds: ['hazard-004', 'hazard-005']
          }
        ]
      }
    ]
  },
  {
    id: 'mock-site-uuid-003',
    name: 'Construction Zone Beta',
    location: 'Karratha, WA',
    createdAt: new Date('2024-03-08').toISOString(),
    createdBy: 'mock-admin-uuid-001',
    accessibleBy: ['mock-admin-uuid-001', 'mock-manager-uuid-006', 'mock-client-uuid-005'],
    drafts: [
      {
        id: 'mock-draft-uuid-003',
        siteId: 'mock-site-uuid-003',
        siteName: 'Construction Zone Beta',
        siteLocation: 'Karratha, WA',
        riskLevel: 'Low',
        completionPercentage: 45,
        createdAt: new Date('2024-03-10').toISOString(),
        updatedAt: new Date('2024-09-30').toISOString(),
        hazards: [
          {
            id: 'hazard-006',
            hazardType: 'Working at Heights',
            hazardStatus: 'Active',
            description: 'Scaffolding work on new infrastructure',
            likelihood: 'Unlikely',
            consequence: 'Major',
            riskRating: 'Medium',
            controls: 'Fall arrest systems, competent persons',
            createdAt: new Date('2024-03-10').toISOString()
          },
          {
            id: 'hazard-007',
            hazardType: 'Electrical Hazards',
            hazardStatus: 'Controlled',
            description: 'Temporary power installations',
            likelihood: 'Rare',
            consequence: 'Catastrophic',
            riskRating: 'Medium',
            controls: 'Licensed electricians, RCD protection',
            createdAt: new Date('2024-04-01').toISOString()
          }
        ],
        mapRectangles: [
          {
            id: 'rect-003',
            topLeft: { lat: -20.72, lng: 116.84 },
            bottomRight: { lat: -20.74, lng: 116.86 },
            color: '#ffff00',
            hazardIds: ['hazard-006', 'hazard-007']
          }
        ]
      }
    ]
  },
  {
    id: 'mock-site-uuid-004',
    name: 'Remote Operations Center',
    location: 'Newman, WA',
    createdAt: new Date('2024-04-22').toISOString(),
    createdBy: 'mock-manager-uuid-006',
    accessibleBy: ['mock-admin-uuid-001', 'mock-manager-uuid-006'],
    drafts: [
      {
        id: 'mock-draft-uuid-004',
        siteId: 'mock-site-uuid-004',
        siteName: 'Remote Operations Center',
        siteLocation: 'Newman, WA',
        riskLevel: 'Low',
        completionPercentage: 30,
        createdAt: new Date('2024-05-01').toISOString(),
        updatedAt: new Date('2024-10-10').toISOString(),
        hazards: [
          {
            id: 'hazard-008',
            hazardType: 'Ergonomic',
            hazardStatus: 'Active',
            description: 'Extended screen time for operators',
            likelihood: 'Likely',
            consequence: 'Minor',
            riskRating: 'Low',
            controls: 'Ergonomic assessments, break schedules',
            createdAt: new Date('2024-05-01').toISOString()
          }
        ],
        mapRectangles: [
          {
            id: 'rect-004',
            topLeft: { lat: -23.35, lng: 119.73 },
            bottomRight: { lat: -23.37, lng: 119.75 },
            color: '#00ff00',
            hazardIds: ['hazard-008']
          }
        ]
      }
    ]
  }
];

export const getMockUsers = (): MockUserSummary[] => {
  return [...MOCK_USERS];
};

export const getMockSites = (): MockSite[] => {
  return [...MOCK_SITES];
};

export const getMockSiteById = (siteId: string): MockSite | null => {
  return MOCK_SITES.find(site => site.id === siteId) || null;
};
